import React, { useContext, useState } from "react";
import { append, omit, remove, update } from "ramda";
import { MediaContext } from "./media-context";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ColumnHeader from "../content/edit/ColumnHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { styled } from "@mui/styles";
import { validate } from "validate.js";
import {
  faArrowUpRightFromSquare,
  faAwardSimple,
  faStar,
} from "@fortawesome/pro-solid-svg-icons";

export const AWARD = "AWARD";
export const REVIEW = "REVIEW";
export const TITLE_MAX_LENGTH = 512;
export const URL_MAX_LENGTH = 2048;

const linkTypeOf = link => (link?.linkType ?? link?.type)?.toUpperCase();

// Users routinely paste URLs with stray whitespace, which validate.js rejects outright.
const trimmed = link => ({
  ...link,
  title: link.title?.trim() ?? "",
  url: link.url?.trim() ?? "",
});

// The invalid-URL message echoes the value back, which needs a ceiling now that
// URLs can run to thousands of characters
const truncate = value =>
  value.length > 60 ? `${value.slice(0, 60)}…` : value;

const TypeChip = styled(Chip, {
  shouldForwardProp: prop => prop !== "type",
})(({ theme, type }) => ({
  width: "100px",
  backgroundColor:
    type === AWARD
      ? theme.palette.primary.mainLight
      : theme.palette.orange.light,
  background:
    type === AWARD
      ? theme.palette.primary.mainLight
      : theme.palette.orange.light,
  color:
    type === AWARD ? theme.palette.primary.main : theme.palette.orange.main,
  fontWeight: theme.typography.fontWeightBold,
  "& .MuiChip-label": {
    fontSize: "1.2rem",
  },
  "& .MuiChip-icon": {
    marginLeft: "12px",
    fontSize: "12px",
  },
}));

const ExternalLinks = () => {
  const context = useContext(MediaContext);
  const [editing, setEditing] = useState(null);

  const handleEdit = (link, index) => _event => {
    setEditing({ index, link });
  };

  const close = () => {
    setEditing(null);
  };

  const save = link => {
    const normalized = { ...omit(["type"], link), linkType: linkTypeOf(link) };
    context.setMedia(prev => ({
      ...prev,
      externalLinks:
        editing.index === null
          ? append(normalized, prev?.externalLinks ?? [])
          : update(editing.index, normalized, prev.externalLinks),
    }));
    close();
  };

  return (
    <Grid container>
      <ColumnHeader
        header="Awards & Reviews"
        paddingBottom={"10px"}
        customButton={
          <Button
            variant="outlined"
            color="primary"
            onClick={() =>
              setEditing({
                index: null,
                link: { title: "", url: "", linkType: AWARD },
              })
            }
          >
            Add Link
          </Button>
        }
      />
      {context.media?.externalLinks &&
        context.media.externalLinks.length > 0 &&
        context.media.externalLinks.map((link, index) => {
          const linkType = linkTypeOf(link);
          return (
            <Grid
              container
              item
              justifyContent="space-between"
              alignItems="center"
              id={`external-link-${index}`}
              key={link.externalLinkId ?? `${index}-${link.title}`}
              sx={theme => ({
                padding: "2px 5px",
                borderRadius: "5px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.mainLight,
                },
              })}
            >
              <Grid item>
                <Link
                  target="_blank"
                  href={link.url}
                  sx={theme => ({
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                  })}
                >
                  {link.title}{" "}
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </Link>
              </Grid>

              <Grid item>
                <TypeChip
                  size="small"
                  color="primary"
                  type={linkType}
                  icon={
                    <FontAwesomeIcon
                      icon={linkType === AWARD ? faAwardSimple : faStar}
                    />
                  }
                  label={linkType}
                />
                <IconButton
                  aria-label={`Edit ${link.title} link`}
                  onClick={handleEdit(link, index)}
                >
                  <FontAwesomeIcon icon={faPen} />
                </IconButton>
                <IconButton
                  aria-label={`Remove ${link.title} link`}
                  onClick={() =>
                    context.setMedia(prev => ({
                      ...prev,
                      externalLinks: remove(index, 1, prev.externalLinks),
                    }))
                  }
                >
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              </Grid>
            </Grid>
          );
        })}

      {editing?.link && (
        <ExternalLinkDialog
          initialLink={editing.link}
          title={editing.index === null ? "Add Link" : "Edit Link"}
          buttonLabel={editing.index === null ? "Add" : "Update"}
          onClose={close}
          onSave={save}
        />
      )}
    </Grid>
  );
};

const StyledSelect = styled(Select)(({ _theme }) => ({
  "label + &": {
    marginTop: "14px",
  },
}));

const constraints = {
  title: {
    length: {
      maximum: TITLE_MAX_LENGTH,
      tooLong: `Display text cannot exceed ${TITLE_MAX_LENGTH} characters`,
    },
    presence: {
      message: "Display text cannot be blank",
      allowEmpty: false,
    },
  },

  url: {
    presence: {
      message: "URL cannot be blank",
      allowEmpty: false,
    },
    length: {
      maximum: URL_MAX_LENGTH,
      tooLong: `URL cannot exceed ${URL_MAX_LENGTH} characters`,
    },
    url: {
      message: value =>
        `'${truncate(value)}' is not a valid URL. Include http:// or https://`,
    },
  },
};

const ExternalLinkDialog = ({
  initialLink,
  onClose,
  onSave,
  title,
  buttonLabel,
}) => {
  const [errors, setErrors] = useState(null);
  const [link, setLink] = useState(initialLink);

  // Normalizing on blur keeps what the user sees identical to what was validated
  const onBlur = prop => _event => {
    const value = link[prop]?.trim() ?? "";
    setLink(prev => ({ ...prev, [prop]: value }));
    setErrors(prev => ({
      ...prev,
      [prop]: validate.single(value, constraints[prop], {
        fullMessages: false,
      }),
    }));
  };

  const onChange = prop => event => {
    const value = event.target.value;
    setLink(prev => ({ ...prev, [prop]: value }));
  };

  const close = () => {
    setErrors(null);
    onClose();
  };

  const save = () => {
    // A value that was pasted and saved without ever blurring still needs trimming
    const normalized = trimmed(link);
    const errors = validate(normalized, constraints, {
      fullMessages: false,
    });
    if (errors) {
      setLink(normalized);
      setErrors(errors);
      return;
    }
    onSave(normalized);
  };
  return (
    <Dialog open onClose={onClose} aria-labelledby="external-link-dialog-title">
      <DialogTitle id="external-link-dialog-title" onClose={close}>
        <Typography component="span" variant="h5">
          {title}
        </Typography>
        <IconButton
          onClick={close}
          aria-label="Close dialog"
          sx={{ position: "absolute", right: 8, top: 15 }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <FormControl fullWidth>
              <TextField
                required
                fullWidth
                id="link-title"
                name="link-title"
                label="Display text"
                value={link.title}
                onBlur={onBlur("title")}
                onChange={onChange("title")}
                inputProps={{
                  "aria-invalid": Boolean(errors?.title),
                  "aria-describedby": errors?.title
                    ? "link-title-error-text"
                    : "link-title-counter",
                }}
              />
              {errors?.title ? (
                <FormHelperText id="link-title-error-text" error>
                  {errors.title[0]}
                </FormHelperText>
              ) : (
                <FormHelperText
                  id="link-title-counter"
                  error={(link?.title?.length ?? 0) > TITLE_MAX_LENGTH}
                  sx={{ right: 0 }}
                >
                  {`${link?.title?.length ?? 0}/${TITLE_MAX_LENGTH} characters`}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs>
            <FormControl required fullWidth>
              <InputLabel id="link-type" htmlFor="link-type-select">
                Type
              </InputLabel>
              <StyledSelect
                labelId="link-type"
                id="link-type-select"
                label="Type"
                value={linkTypeOf(link) ?? AWARD}
                onChange={onChange("linkType")}
              >
                <MenuItem value={AWARD}>Award</MenuItem>
                <MenuItem value={REVIEW}>Review</MenuItem>
              </StyledSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: 2 }}>
            <FormControl fullWidth>
              <TextField
                required
                fullWidth
                id="link-url"
                name="link-url"
                label="URL"
                placeholder="https://..."
                value={link.url}
                onChange={onChange("url")}
                onBlur={onBlur("url")}
                inputProps={{
                  "aria-invalid": Boolean(errors?.url),
                  "aria-describedby": errors?.url
                    ? "link-url-error-text"
                    : "link-url-counter",
                }}
              />
              {errors?.url ? (
                <FormHelperText id="link-url-error-text" error>
                  {errors.url[0]}
                </FormHelperText>
              ) : (
                <FormHelperText
                  id="link-url-counter"
                  error={(link?.url?.length ?? 0) > URL_MAX_LENGTH}
                  sx={{ right: 0 }}
                >
                  {`${link?.url?.length ?? 0}/${URL_MAX_LENGTH} characters`}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={save}>
          {buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExternalLinks;
